import { useEffect, useRef, useSyncExternalStore, type ReactNode } from 'react';
import { CircleHelp, TriangleAlert } from 'lucide-react';
import { Modal } from './Modal';
import styles from '../../LootboxPage.module.scss';
import ws from '../WorldsPage.module.scss';

// ============================================================
// Confirmation d'action de la page Mondes (remplace window.confirm)
// ============================================================
// Un hôte unique (ConfirmHost, monté par WorldsPage) et une file d'attente : un site d'appel fait
// `if (!(await confirmAction({...}))) return;` sans rien rendre. Deux demandes simultanées ne
// s'écrasent pas : la seconde attend la première. Au démontage de l'hôte (départ de la page), les
// demandes en attente se résolvent à false. Sans hôte monté, la réponse est false tout de suite.
// ============================================================

export interface ConfirmOptions {
  title: string;
  /** Phrase principale (le nom visé en <strong> de préférence). */
  message: ReactNode;
  /** Encadré sous le message : conséquence, position actuelle, rappel d'un refus possible. */
  details?: ReactNode;
  /** Défaut « Supprimer ». */
  confirmLabel?: string;
  /** Défaut « Annuler ». */
  cancelLabel?: string;
  /** danger (défaut) : bouton rouge, focus initial sur Annuler ; primary : focus sur l'action. */
  tone?: 'danger' | 'primary';
}

interface Pending {
  id: number;
  options: ConfirmOptions;
  resolve: (ok: boolean) => void;
}

let queue: Pending[] = [];
let seq = 0;
let hosts = 0;
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((l) => l());
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

function current(): Pending | null {
  return queue[0] ?? null;
}

function settle(id: number, ok: boolean): void {
  const p = queue.find((x) => x.id === id);
  if (!p) return;
  queue = queue.filter((x) => x.id !== id);
  p.resolve(ok);
  emit();
}

/** Demande une confirmation ; true si l'utilisateur confirme. */
export function confirmAction(options: ConfirmOptions): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    if (hosts === 0) { resolve(false); return; }
    queue = [...queue, { id: ++seq, options, resolve }];
    emit();
  });
}

function ConfirmDialog({ request }: { request: Pending }) {
  const { title, message, details, confirmLabel = 'Supprimer', cancelLabel = 'Annuler', tone = 'danger' } = request.options;
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const confirmRef = useRef<HTMLButtonElement | null>(null);

  // Focus initial selon le ton ; au départ, le focus revient à l'élément qui l'avait (bouton de ligne).
  useEffect(() => {
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    (tone === 'danger' ? cancelRef : confirmRef).current?.focus();
    return () => { if (before?.isConnected) before.focus(); };
  }, [tone]);

  const danger = tone === 'danger';
  return (
    <Modal size="sm" title={title} onClose={() => settle(request.id, false)}
      icon={danger ? <TriangleAlert size={16} /> : <CircleHelp size={16} />} iconTone={danger ? 'danger' : 'primary'}
      footer={(
        <>
          <button ref={cancelRef} type="button" className={styles.secondaryBtn} onClick={() => settle(request.id, false)}>
            {cancelLabel}
          </button>
          <button ref={confirmRef} type="button" className={`${styles.primaryBtn} ${danger ? ws.btnDanger : ''}`}
            onClick={() => settle(request.id, true)}>
            {confirmLabel}
          </button>
        </>
      )}>
      <div className={ws.confirmMessage}>{message}</div>
      {details && <div className={ws.confirmDetails}>{details}</div>}
    </Modal>
  );
}

/** Hôte des confirmations : monté une seule fois, par WorldsPage. */
export function ConfirmHost() {
  const request = useSyncExternalStore(subscribe, current, current);

  useEffect(() => {
    hosts++;
    return () => {
      hosts--;
      if (hosts > 0) return;
      const pending = queue;
      queue = [];
      pending.forEach((p) => p.resolve(false));
      emit();
    };
  }, []);

  if (!request) return null;
  return <ConfirmDialog key={request.id} request={request} />;
}
